import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { ArrowLeft } from "lucide-react";

const PAGES = {
    confidentialite: {
        title: "Politique de confidentialité",
        eyebrow: "RGPD",
        intro:
            "CapDistrib (marque commerciale de Flowtex SAS) accorde une importance fondamentale à la protection de tes données personnelles. Cette politique détaille comment nous les collectons, utilisons, conservons et protégeons, conformément au Règlement Général sur la Protection des Données (UE) 2016/679.",
        sections: [
            {
                h: "1. Responsable du traitement",
                p: "Le responsable du traitement est Flowtex SAS, opérant la marque commerciale « CapDistrib ». Contact RGPD : contact@capdistrib.com.",
            },
            {
                h: "2. Données collectées",
                p: "Via le formulaire de candidature partenaire : prénom, nom, email professionnel, téléphone, pays/région d'activité, niveau souhaité, type de réseau ciblé, description du réseau existant, source de connaissance de CapDistrib, consentement explicite. Aucune donnée sensible (santé, religion, opinion politique, etc.) n'est demandée.",
            },
            {
                h: "3. Finalités",
                p: "Les données sont collectées exclusivement pour : (a) traiter ta candidature partenaire, (b) te contacter pour un entretien de qualification, (c) gérer la relation contractuelle si ta candidature aboutit. Aucune donnée n'est utilisée à des fins de prospection externe ou cédée à des tiers commerciaux.",
            },
            {
                h: "4. Base légale",
                p: "Le traitement repose sur ton consentement explicite (case à cocher du formulaire) et, le cas échéant, sur l'exécution de mesures précontractuelles à ta demande (art. 6.1.a et 6.1.b RGPD).",
            },
            {
                h: "5. Durée de conservation",
                p: "Les candidatures actives sont conservées pendant la durée de la relation contractuelle. Les candidatures non retenues sont conservées 24 mois maximum à compter du dernier contact, puis supprimées ou anonymisées.",
            },
            {
                h: "6. Destinataires",
                p: "Tes données sont accessibles uniquement à l'équipe CapDistrib chargée du recrutement partenaires et à nos sous-traitants techniques (hébergement, base de données) dans le strict cadre de leur mission. Aucun transfert hors UE n'est effectué sans garanties appropriées.",
            },
            {
                h: "7. Cookies et mesure d'audience",
                p: "Nous utilisons un outil de mesure d'audience (PostHog) pour comprendre comment les visiteurs naviguent sur le site. Aucun cookie de tracking n'est déposé tant que tu n'as pas explicitement accepté via le bandeau de consentement. Tu peux retirer ton consentement à tout moment en effaçant le stockage local de ton navigateur.",
            },
            {
                h: "8. Tes droits",
                p: "Conformément au RGPD, tu disposes des droits suivants : accès, rectification, effacement, limitation, opposition, portabilité, retrait du consentement. Pour les exercer, écris à contact@capdistrib.com en précisant ta demande. Une réponse te sera apportée sous 30 jours. Tu peux également déposer une réclamation auprès de la CNIL (www.cnil.fr).",
            },
            {
                h: "9. Sécurité",
                p: "Tes données sont stockées sur des serveurs sécurisés au sein de l'Union européenne. L'accès est restreint par authentification et chiffrement en transit (HTTPS).",
            },
            {
                h: "10. Modifications",
                p: "Cette politique peut être mise à jour. La date de dernière modification est indiquée en bas de page. En cas de changement substantiel, nous t'en informerons.",
            },
        ],
        updated: "Mise à jour : décembre 2025",
    },
    "mentions-legales": {
        title: "Mentions légales",
        eyebrow: "Légal",
        intro:
            "Conformément à la loi n°2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique, voici les mentions légales du site CapDistrib.",
        sections: [
            {
                h: "Éditeur du site",
                p: "Le site capdistrib.com est édité par Flowtex SAS, exploitant la marque commerciale « CapDistrib ». Pour toute demande, contact@capdistrib.com.",
            },
            {
                h: "Directeur de la publication",
                p: "Le représentant légal de Flowtex SAS.",
            },
            {
                h: "Hébergement",
                p: "Le site est hébergé sur une infrastructure cloud située dans l'Union européenne. Les détails techniques sont disponibles sur demande à contact@capdistrib.com.",
            },
            {
                h: "Propriété intellectuelle",
                p: "L'ensemble des contenus du site (textes, images, logos, identité visuelle CapDistrib) est protégé par le droit d'auteur. Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.",
            },
            {
                h: "Marques",
                p: "« CapDistrib » est une marque commerciale exploitée par Flowtex SAS. La plateforme technique sous-jacente est propulsée par ePaaS.io.",
            },
            {
                h: "Crédits photos",
                p: "Photographies d'illustration sous licence Unsplash. Aucune photo ne représente un partenaire CapDistrib réel.",
            },
        ],
        updated: "Mise à jour : décembre 2025",
    },
    cgu: {
        title: "Conditions générales d'utilisation",
        eyebrow: "Légal",
        intro:
            "Les présentes CGU régissent l'utilisation du site capdistrib.com. En accédant au site, tu acceptes ces conditions.",
        sections: [
            {
                h: "1. Objet du site",
                p: "Le site présente le programme partenaire B2B CapDistrib en distribution de produits digitaux. Il permet à des indépendants qualifiés de candidater pour rejoindre ce programme.",
            },
            {
                h: "2. Accès au site",
                p: "L'accès au site est libre et gratuit. CapDistrib se réserve le droit de suspendre l'accès en cas de maintenance ou de force majeure.",
            },
            {
                h: "3. Candidature",
                p: "La candidature au programme partenaire est gratuite et n'engage à rien. CapDistrib se réserve le droit de refuser une candidature sans avoir à motiver sa décision. Aucun droit acquis ne résulte du simple dépôt d'une candidature.",
            },
            {
                h: "4. Rémunération",
                p: "Les estimations de revenus présentées sur le site (simulateur, scénarios, projection 36 mois) sont indicatives et basées sur des hypothèses standards. Elles ne constituent ni une promesse, ni une garantie de gain. Les commissions effectives dépendent du volume réel et du mix catalogue activé sur chaque réseau.",
            },
            {
                h: "5. Responsabilité",
                p: "CapDistrib s'efforce d'assurer l'exactitude des informations diffusées mais ne peut être tenu responsable des erreurs, omissions ou indisponibilités. L'utilisateur reste responsable de ses propres décisions commerciales.",
            },
            {
                h: "6. Données personnelles",
                p: "Le traitement des données est détaillé dans notre Politique de confidentialité.",
            },
            {
                h: "7. Droit applicable",
                p: "Les présentes CGU sont régies par le droit français. Tout litige relèvera de la compétence des tribunaux français.",
            },
        ],
        updated: "Mise à jour : décembre 2025",
    },
};

export default function LegalPage({ slug }) {
    const page = PAGES[slug];
    if (!page) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cap-light text-cap-ink">
                <p>Page introuvable.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cap-light text-cap-ink">
            <PageMeta
                title={`${page.title} — CapDistrib`}
                description={page.intro.slice(0, 158)}
                canonicalPath={`/${slug}`}
                noindex={false}
            />
            <header className="bg-cap-ink text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between">
                    <Link to="/" data-testid="legal-home-link">
                        <Logo variant="white" />
                    </Link>
                    <Link
                        to="/"
                        className="text-[13.5px] text-white/70 hover:text-white inline-flex items-center gap-1.5"
                    >
                        <ArrowLeft size={14} /> Retour au site
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <p className="eyebrow">{page.eyebrow}</p>
                <h1
                    className="h-display text-4xl sm:text-5xl font-bold mt-3 text-cap-ink"
                    data-testid="legal-title"
                >
                    {page.title}
                </h1>
                <p className="mt-6 text-lg text-cap-textMuted leading-relaxed">
                    {page.intro}
                </p>
                <div className="mt-12 space-y-8">
                    {page.sections.map((s, i) => (
                        <section key={i}>
                            <h2 className="h-display text-xl font-semibold text-cap-ink">
                                {s.h}
                            </h2>
                            <p className="mt-3 text-[15.5px] text-cap-textMuted leading-relaxed">
                                {s.p}
                            </p>
                        </section>
                    ))}
                </div>
                <p className="mt-16 text-[13px] text-cap-textMuted italic">
                    {page.updated}
                </p>
            </main>

            <Footer />
        </div>
    );
}
